/**
 * Blobbi SVG sanitization — defense-in-depth for the Blobbi rendering pipeline.
 *
 * This sanitizer is SEPARATE from sanitizeSvg.ts (used for untrusted user SVGs).
 * The two sanitizers have different threat models and MUST NOT share configuration:
 *
 * - sanitizeSvg.ts: For untrusted user content (stickers, drawings). Very strict.
 * - sanitizeBlobbiSvg.ts: For Blobbi pipeline output. Broader allowlist for animations.
 *
 * The Blobbi visual pipeline (customizers, eye-animation, emotions) performs
 * extensive string manipulation on SVG markup before injecting it via
 * `dangerouslySetInnerHTML`. While user inputs are validated at the parsing
 * boundary (normalizeHexColor, instanceId sanitization), this sanitizer acts
 * as a safety net at the *output* boundary.
 *
 * IMPORTANT: This file uses its own isolated DOMPurify instance to avoid
 * polluting the global DOMPurify used by other sanitizers.
 *
 * Blocked: <script>, <foreignObject>, <iframe>, <embed>, <object>, <a>,
 *          <use>, <image>, all event handlers (on*), href/xlink:href.
 */

import DOMPurify from 'dompurify';

// ─────────────────────────────────────────────────────────────────────────────
// ISOLATED DOMPURIFY INSTANCE
// ─────────────────────────────────────────────────────────────────────────────
// We create a separate DOMPurify instance for Blobbi SVGs to avoid polluting
// the global DOMPurify with Blobbi-specific hooks. This ensures that:
// 1. The generic sanitizeSvg() remains strict and unaffected
// 2. Blobbi-specific data-* attribute handling doesn't leak to other sanitizers
// ─────────────────────────────────────────────────────────────────────────────

const blobbiPurify = DOMPurify();

/**
 * Hook to allow data-* attributes for the eye animation system.
 *
 * When ALLOWED_ATTR is specified, DOMPurify switches to strict whitelist mode
 * and would strip all attributes not in the list. The eye animation system
 * uses data-* attributes for storing geometry/state:
 *
 * - data-cx, data-cy: Eye center coordinates
 * - data-eye-top, data-eye-bottom: Eye bounds for clip-path animation
 * - data-clip-height: Full height of clip rect
 * - data-clip-id: Reference to the clipPath element ID
 *
 * These are generated by our own code (eye-animation.ts), not user input.
 *
 * SECURITY NOTE: data-* attributes cannot execute JavaScript directly.
 * They are inert storage and only become active if JavaScript explicitly
 * reads and acts on them. Our eye animation code only reads numeric values
 * and element IDs from these attributes.
 */
blobbiPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (data.attrName.startsWith('data-')) {
    data.allowedAttributes[data.attrName] = true;
  }
});

// ─────────────────────────────────────────────────────────────────────────────
// ALLOWED TAGS
// ─────────────────────────────────────────────────────────────────────────────
// These are SVG elements that the Blobbi pipeline legitimately produces.
// Each category is documented with its use case.
// ─────────────────────────────────────────────────────────────────────────────

const BLOBBI_ALLOWED_TAGS = [
  // Structural elements - basic SVG building blocks
  'svg',
  'g',
  'defs',
  'path',
  'circle',
  'ellipse',
  'rect',
  'line',
  'polyline',
  'polygon',

  // Gradient elements - used for body colors, eye shading, tear drops
  'radialGradient',
  'linearGradient',
  'stop',

  // Clipping and masking - used for eye blink animation
  'clipPath',
  'mask',

  // SMIL Animation elements - used for:
  // - Eye blink clip-path animation (animate on rect y/height)
  // - Tear drop falling animation (animate on cy/opacity)
  // - Sleepy mouth morphing (animate on path d)
  // - Dizzy spiral rotation (animateTransform type="rotate")
  // - Anger rise gradient animation (animate on stop offset/opacity)
  'animate',
  'animateTransform',
  'animateMotion',

  // Text elements - used for sleepy "Zzz" animation
  'text',
  'tspan',

  // ───────────────────────────────────────────────────────────────────────────
  // REVIEW REQUIRED: <style> tag
  // ───────────────────────────────────────────────────────────────────────────
  // The emotion system uses <style> tags for CSS @keyframes animations:
  // - Dizzy spiral rotation (@keyframes in CSS as fallback)
  // - Eyebrow bounce animation
  // - Star eyes pulse animation
  // - Sleepy Zzz fade animation
  //
  // RISK: <style> tags can contain CSS that affects elements outside the SVG
  // if selectors are not properly scoped. However, our emotion system only
  // generates class-based selectors like ".blobbi-zzz" which are scoped to
  // elements within the Blobbi SVG.
  //
  // The content is generated by our code (emotions.ts), not user input.
  // ───────────────────────────────────────────────────────────────────────────
  'style',
];

// ─────────────────────────────────────────────────────────────────────────────
// ALLOWED ATTRIBUTES
// ─────────────────────────────────────────────────────────────────────────────
// Attributes that the Blobbi pipeline uses. Grouped by category.
// ─────────────────────────────────────────────────────────────────────────────

const BLOBBI_ALLOWED_ATTRS = [
  // Structural attributes
  'xmlns',
  'viewBox',
  'preserveAspectRatio',
  'width',
  'height',
  'id',
  'class',

  // Presentation attributes - colors, strokes, opacity
  'fill',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-opacity',
  'fill-opacity',
  'fill-rule',
  'opacity',

  // Transform attributes - used for eye tracking, dizzy rotation
  'transform',
  'transform-origin',
  'transform-box',

  // ───────────────────────────────────────────────────────────────────────────
  // REVIEW REQUIRED: style attribute
  // ───────────────────────────────────────────────────────────────────────────
  // Used by eye-animation.ts for inline styles on eye groups:
  //   style="transform-box: fill-box; transform-origin: center;"
  //
  // RISK: Inline styles can contain url() references or other potentially
  // dangerous CSS. However, our code only generates the specific transform
  // properties listed above.
  //
  // The content is generated by our code, not user input.
  // ───────────────────────────────────────────────────────────────────────────
  'style',

  // Clipping and masking references
  'clip-path',
  'clip-rule',
  'mask',

  // Geometry attributes for shapes
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'd',
  'points',

  // Gradient attributes
  'offset',
  'stop-color',
  'stop-opacity',
  'gradientUnits',
  'gradientTransform',
  'patternUnits',
  'spreadMethod',
  'fx',
  'fy',

  // SMIL Animation attributes - comprehensive list for all animation types
  'attributeName',   // Which attribute to animate
  'attributeType',   // CSS or XML
  'values',          // Keyframe values (e.g., "20;40;20")
  'keyTimes',        // Timing for keyframes (e.g., "0;0.5;1")
  'keySplines',      // Easing curves for smooth animation
  'keyPoints',       // Points along motion path
  'calcMode',        // Interpolation mode (spline, linear, discrete, paced)
  'dur',             // Duration (e.g., "8s")
  'begin',           // Start time or event
  'end',             // End time or event
  'repeatCount',     // Number of repetitions (e.g., "indefinite")
  'repeatDur',       // Total duration for repetitions
  'fill',            // What to do after animation ends (freeze, remove)
  'from',            // Starting value
  'to',              // Ending value
  'by',              // Relative change
  'type',            // For animateTransform: rotate, scale, translate, etc.
  'additive',        // Whether to add to or replace base value
  'accumulate',      // Whether values accumulate across repetitions
  'path',            // Motion path for animateMotion

  // Text attributes - for sleepy "Zzz" text
  'text-anchor',
  'dominant-baseline',
  'font-family',
  'font-size',
  'font-weight',
];

// ─────────────────────────────────────────────────────────────────────────────
// FORBIDDEN TAGS
// ─────────────────────────────────────────────────────────────────────────────
// Elements that are explicitly blocked even if they appear in the input.
// ─────────────────────────────────────────────────────────────────────────────

const BLOBBI_FORBIDDEN_TAGS = [
  'script',        // JavaScript execution
  'foreignObject', // Can embed arbitrary HTML/CSS
  'iframe',        // Can load external content
  'embed',         // Can load external content
  'object',        // Can load external content
  'use',           // Can reference external SVG content
  'image',         // Can load external images (disabled for Blobbi)
  'a',             // Links (not needed, could be used for phishing)
];

// ─────────────────────────────────────────────────────────────────────────────
// FORBIDDEN ATTRIBUTES
// ─────────────────────────────────────────────────────────────────────────────
// Attributes that are explicitly blocked to prevent XSS.
// ─────────────────────────────────────────────────────────────────────────────

const BLOBBI_FORBIDDEN_ATTRS = [
  // DOM Event handlers - comprehensive list
  // Any of these could execute arbitrary JavaScript
  'onload',
  'onerror',
  'onclick',
  'ondblclick',
  'onmousedown',
  'onmouseup',
  'onmouseover',
  'onmouseout',
  'onmouseenter',
  'onmouseleave',
  'onmousemove',
  'onfocus',
  'onblur',
  'onkeydown',
  'onkeyup',
  'onkeypress',
  'onchange',
  'oninput',
  'onsubmit',
  'onreset',
  'onscroll',
  'onwheel',
  'oncopy',
  'oncut',
  'onpaste',
  'ondrag',
  'ondragend',
  'ondragenter',
  'ondragleave',
  'ondragover',
  'ondragstart',
  'ondrop',

  // CSS Animation/Transition events
  'onanimationend',
  'onanimationstart',
  'onanimationiteration',
  'ontransitionend',
  'ontransitionstart',
  'ontransitioncancel',

  // SVG SMIL events - can trigger JavaScript
  'onbegin',
  'onend',
  'onrepeat',

  // Touch events
  'ontouchstart',
  'ontouchend',
  'ontouchmove',
  'ontouchcancel',

  // Link attributes - Blobbi uses url(#id) for internal refs, not href
  'href',
  'xlink:href',
];

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURATION
// ─────────────────────────────────────────────────────────────────────────────

/** Maximum SVG string length (512 KB). Blobbi SVGs with all emotion overlays are ~30 KB. */
const MAX_SVG_LENGTH = 512 * 1024;

// ─────────────────────────────────────────────────────────────────────────────
// SANITIZER FUNCTION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sanitize a Blobbi SVG string before injection via `dangerouslySetInnerHTML`.
 *
 * This is the output-boundary safety net for the Blobbi rendering pipeline.
 * It strips scripts, event handlers, and other dangerous constructs while
 * preserving the gradients, animations, clip paths, and CSS that the pipeline
 * legitimately produces.
 *
 * ISOLATION: Uses a dedicated DOMPurify instance (blobbiPurify) to avoid
 * affecting the global DOMPurify used by sanitizeSvg.ts.
 *
 * Key features:
 * - All data-* attributes are allowed (via isolated hook)
 * - SMIL animation elements and attributes are preserved
 * - CSS @keyframes in <style> tags are allowed (for emotions)
 * - Event handlers and href attributes are blocked
 *
 * What this sanitizer catches:
 * - Any <script> tags that might be injected
 * - Event handlers like onclick, onload, etc.
 * - Links via href/xlink:href
 * - Dangerous elements like foreignObject, iframe, etc.
 */
export function sanitizeBlobbiSvg(dirty: string): string {
  if (dirty.length > MAX_SVG_LENGTH) {
    if (import.meta.env.DEV) {
      console.warn('[sanitizeBlobbiSvg] SVG exceeds max length, rejecting');
    }
    return '';
  }

  // Use the isolated blobbiPurify instance, NOT the global DOMPurify
  // We do NOT use USE_PROFILES because the SVG profile has its own
  // internal whitelist that conflicts with our explicit ALLOWED_TAGS/ALLOWED_ATTR.
  return blobbiPurify.sanitize(dirty, {
    ALLOWED_TAGS: BLOBBI_ALLOWED_TAGS,
    ALLOWED_ATTR: BLOBBI_ALLOWED_ATTRS,
    FORBID_TAGS: BLOBBI_FORBIDDEN_TAGS,
    FORBID_ATTR: BLOBBI_FORBIDDEN_ATTRS,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  }) as string;
}

// ─────────────────────────────────────────────────────────────────────────────
// DIFFERENCES FROM GENERIC sanitizeSvg()
// ─────────────────────────────────────────────────────────────────────────────
//
// Tags that Blobbi allows but generic does NOT:
// - defs, radialGradient, linearGradient, stop (gradients)
// - clipPath, mask (clipping/masking)
// - animate, animateTransform, animateMotion (SMIL animation)
// - text, tspan (text rendering)
// - style (CSS @keyframes) ← REVIEW REQUIRED
//
// Attributes that Blobbi allows but generic does NOT:
// - id, class (element identification)
// - preserveAspectRatio, transform-origin, transform-box
// - style (inline styles) ← REVIEW REQUIRED
// - clip-path, clip-rule, mask (clipping references)
// - fill-rule
// - All gradient attributes (offset, stop-color, stop-opacity, etc.)
// - All SMIL animation attributes (attributeName, values, dur, etc.)
// - Text attributes (font-family, font-size, etc.)
// - data-* attributes (via hook) ← NOT allowed in generic
//
// The generic sanitizer is intentionally stricter because it handles
// untrusted user content (stickers, drawings), while Blobbi handles
// content generated by our own pipeline with validated inputs.
// ─────────────────────────────────────────────────────────────────────────────
